import { createContext, useContext, useReducer, useCallback, useMemo, useEffect, FC, ReactNode } from 'react';
import { statementsFiltersSelector } from '@algoan/dashboard/store/selectors';
import { deleteDateFilter } from '@algoan/dashboard/store/slice';
import { useDispatch, useSelector } from '@algoan/dashboard/store/store';

export interface BarData {
  date: number;
  balance: number;
  difference: number;
  x: number;
  index: number;
}

export enum RangeStatus {
  IDLE = 'IDLE',
  SELECT_ORIGIN_DATA = 'SELECT_ORIGIN_DATE',
  SELECT_END_DATA = 'SELECT_END_DATE',
  START_UPDATE_ORIGIN = 'START_UPDATE_ORIGIN',
  START_UPDATE_END = 'START_UPDATE_END',
}

interface BankHistorySelectionReducerState {
  rangeStatus: RangeStatus;
  barWidth?: number;
  originData?: BarData;
  endData?: BarData;
}

type BankHistorySelectionReducerAction =
  | { type: 'ADD_ORIGIN_DATA'; date: number; balance: number; difference: number; x: number; index: number }
  | { type: 'ADD_END_DATA'; date: number; balance: number; difference: number; x: number; index: number }
  | { type: 'UPDATE_ORIGIN_DATA'; date: number; balance: number; difference: number; x: number; index: number }
  | { type: 'START_ORIGIN_UPDATE' }
  | { type: 'START_END_UPDATE' }
  | { type: 'SELECTION_END' }
  | { type: 'SELECTION_RESET' };

const initialState: BankHistorySelectionReducerState = {
  rangeStatus: RangeStatus.IDLE,
};

const bankHistorySelectionReducer = (
  state: BankHistorySelectionReducerState,
  action: BankHistorySelectionReducerAction,
): BankHistorySelectionReducerState => {
  switch (action.type) {
    case 'ADD_ORIGIN_DATA': {
      const originData = {
        date: action.date,
        balance: action.balance,
        difference: action.difference,
        x: action.x,
        index: action.index,
      };

      return {
        ...state,
        rangeStatus: RangeStatus.SELECT_END_DATA,
        originData,
        endData: undefined,
      };
    }
    case 'ADD_END_DATA': {
      const endData = {
        date: action.date,
        balance: action.balance,
        difference: action.difference,
        x: action.x,
        index: action.index,
      };

      if (endData.date === state.endData?.date) return { ...state };
      if (endData.date === state.originData?.date)
        return { ...state, endData: undefined, rangeStatus: RangeStatus.SELECT_END_DATA };
      return { ...state, endData, rangeStatus: RangeStatus.SELECT_END_DATA };
    }
    case 'UPDATE_ORIGIN_DATA': {
      const originData = {
        date: action.date,
        balance: action.balance,
        difference: action.difference,
        x: action.x,
        index: action.index,
      };

      if (originData.date === state.originData?.date) return { ...state };
      if (originData.date === state.endData?.date)
        return { ...state, originData, rangeStatus: RangeStatus.SELECT_ORIGIN_DATA };
      return { ...state, originData, rangeStatus: RangeStatus.SELECT_ORIGIN_DATA };
    }
    case 'START_ORIGIN_UPDATE': {
      return { ...state, rangeStatus: RangeStatus.START_UPDATE_ORIGIN };
    }
    case 'START_END_UPDATE': {
      return { ...state, rangeStatus: RangeStatus.START_UPDATE_END };
    }
    case 'SELECTION_END': {
      return { ...state, rangeStatus: RangeStatus.IDLE };
    }
    case 'SELECTION_RESET': {
      return initialState;
    }
  }
};

interface UseBankHistorySelection {
  rangeStatus: RangeStatus;
  originData?: BarData;
  endData?: BarData;
  rangeDates: [number, number];
  setOriginData: (props: BarData) => void;
  setEndData: (props: BarData) => void;
  updateOriginData: (props: BarData) => void;
  unselectData: () => void;
  setSelectionEnd: () => void;
  resetDaysSelection: () => void;
}

type BankHistorySelection = UseBankHistorySelection | undefined;

const BankHistorySelectionContext = createContext<BankHistorySelection>(undefined);

export const useBankHistorySelection = (): UseBankHistorySelection => {
  const context = useContext(BankHistorySelectionContext);

  if (!context) {
    throw new Error('useBankHistoryConfig should be used within a BankHistoryConfigProvider');
  }
  return context;
};

export const BankHistorySelectionProvider: FC<{ children: ReactNode }> = ({ ...props }) => {
  const [bankHistorySelection, dispatch] = useReducer(bankHistorySelectionReducer, initialState);
  const reduxDispatch = useDispatch();
  const selectedFilters = useSelector(statementsFiltersSelector);

  const { transactionDays: selectedTransactionDays } = selectedFilters;

  const rangeStatus = useMemo(() => bankHistorySelection.rangeStatus, [bankHistorySelection]);

  const originData = useMemo(() => bankHistorySelection.originData, [bankHistorySelection]);

  const endData = useMemo(() => bankHistorySelection.endData, [bankHistorySelection]);

  const rangeDates = useMemo<[number, number]>(() => {
    const defaultArray: [number, number] = [0, 0];
    if (!bankHistorySelection.originData || !bankHistorySelection.endData) return defaultArray;
    const selectedDatesArray: [number, number] = [
      bankHistorySelection.originData.date,
      bankHistorySelection.endData.date,
    ];
    return selectedDatesArray.sort((a, b) => a - b);
  }, [bankHistorySelection]);

  const setOriginData = useCallback(
    (originProps: BarData) => {
      if (originData?.date === originProps.date && !endData) dispatch({ type: 'START_END_UPDATE' });
      else if (originData?.date === originProps.date) dispatch({ type: 'START_ORIGIN_UPDATE' });
      else if (endData?.date === originProps.date) dispatch({ type: 'START_END_UPDATE' });
      else dispatch({ type: 'ADD_ORIGIN_DATA', ...originProps });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [endData, originData],
  );

  const setEndData = useCallback((endProps: BarData) => dispatch({ type: 'ADD_END_DATA', ...endProps }), []);

  const updateOriginData = useCallback(
    (newOriginProps: BarData) => dispatch({ type: 'UPDATE_ORIGIN_DATA', ...newOriginProps }),
    [],
  );

  const setSelectionEnd = useCallback(() => dispatch({ type: 'SELECTION_END' }), []);

  const resetDaysSelection = useCallback(() => dispatch({ type: 'SELECTION_RESET' }), []);

  const unselectData = useCallback(
    () => {
      resetDaysSelection();
      reduxDispatch(deleteDateFilter());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (!selectedTransactionDays) resetDaysSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTransactionDays]);

  return (
    <BankHistorySelectionContext.Provider
      value={{
        rangeStatus,
        originData,
        endData,
        rangeDates,
        setOriginData,
        setEndData,
        updateOriginData,
        unselectData,
        setSelectionEnd,
        resetDaysSelection,
      }}
      {...props}
    />
  );
};
